/**
 * Block 13 PR-G Phase 4A — Section «Стороны» (Заказчик + Исполнитель).
 *
 * Одно поле ФИО/Наименование для каждой стороны + опциональные «В лице» / «На
 * основании» (для юр.лиц через представителя). PRD §10.13.2 decision:
 * НЕТ chip-segment Физлицо/Юрлицо — clientType выводится на сервере по
 * маркерам «ООО»/«ТОО»/«АО»/«ИП»/«БИН»/«КПП»/«ОГРН»/«ПИНФЛ» в названии.
 *
 * **P-009 hint:** заказчик содержит PII (полное имя). Compose hint footnote
 * в SectionRequisites (Phase 4B), не здесь — name сам по себе не считается
 * чувствительным (имя пользователь может указать публично; чувствительными
 * являются ИИН/паспорт/адрес проживания в Requisites).
 *
 * Reference: prototype `screen-2.13.2-contract.html` lines 396-422.
 */

import { type Dispatch } from 'react';

import { SectionCard } from '@/features/calculator/anketa/components/SectionCard';
import { SectionHeader } from '@/features/calculator/anketa/components/SectionHeader';

import { MissingFieldsBadge } from '../components/MissingFieldsBadge';
import type { ContractAction, ContractFormState } from '../state/contractReducer';
import { selectPartiesSectionMissing } from '../state/contractSelectors';

const INPUT_CLASS =
  'w-full min-h-tap px-3 rounded-xl border-[1.5px] border-accent bg-card text-base text-ink ' +
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none placeholder:text-ink/40';

const LABEL_CLASS = 'block text-sm font-semibold text-ink/80 mb-1';
const HINT_CLASS = 'text-xs text-ink/60 font-normal ml-1';

export interface SectionPartiesProps {
  state: ContractFormState;
  dispatch: Dispatch<ContractAction>;
}

export function SectionParties({ state, dispatch }: SectionPartiesProps) {
  // Phase 6.5 #14 — section badge при пустых required полях (customerName /
  // executorName). Подсказывает пользователю где заполнять для разблокировки
  // «Скачать PDF» / «Переслать клиенту».
  const showMissing = selectPartiesSectionMissing(state);

  return (
    <SectionCard testId="contract-section-parties">
      <div className="flex items-center gap-2 mb-3.5">
        <SectionHeader className="mb-0">Стороны</SectionHeader>
        {showMissing ? (
          <MissingFieldsBadge testId="contract-section-parties-missing-badge" />
        ) : null}
      </div>

      {/* Заказчик — Phase 6.5 #4 — добавили «В лице» + «На основании»
          per Aibek decision (mirror executor structure). Поля
          customerRepName / customerRepBasis уже существуют в ContractDraft
          schema (Phase 1), здесь только UI exposure. Для физлиц поля остаются
          пустыми — пользователь заполняет только когда заказчик — юр.лицо. */}
      <div className="mb-4">
        <p className="text-xs font-bold text-ink uppercase tracking-wide mb-2">Заказчик</p>
        <div className="flex flex-col gap-3">
          <div>
            <label htmlFor="customer-name" className={LABEL_CLASS}>
              ФИО / Наименование
            </label>
            <input
              id="customer-name"
              type="text"
              value={state.customerName}
              onChange={(e) =>
                dispatch({
                  type: 'SET_FIELD',
                  update: { path: 'customerName', value: e.target.value },
                })
              }
              aria-label="ФИО или наименование заказчика"
              data-testid="customer-name-input"
              className={INPUT_CLASS}
              placeholder="Иванов Иван Иванович / ТОО «Мебельный мир»"
            />
          </div>

          <div>
            <label htmlFor="customer-rep-name" className={LABEL_CLASS}>
              В лице
              <span className={HINT_CLASS}>(если через представителя)</span>
            </label>
            <input
              id="customer-rep-name"
              type="text"
              value={state.customerRepName}
              onChange={(e) =>
                dispatch({
                  type: 'SET_FIELD',
                  update: { path: 'customerRepName', value: e.target.value },
                })
              }
              aria-label="Представитель заказчика"
              data-testid="customer-rep-name-input"
              className={INPUT_CLASS}
              placeholder="Иванова И.И."
            />
          </div>

          <div>
            <label htmlFor="customer-rep-basis" className={LABEL_CLASS}>
              На основании
              <span className={HINT_CLASS}>(Устава / Доверенности и т.п.)</span>
            </label>
            <input
              id="customer-rep-basis"
              type="text"
              value={state.customerRepBasis}
              onChange={(e) =>
                dispatch({
                  type: 'SET_FIELD',
                  update: { path: 'customerRepBasis', value: e.target.value },
                })
              }
              aria-label="Основание полномочий представителя заказчика"
              data-testid="customer-rep-basis-input"
              className={INPUT_CLASS}
              placeholder="действующего на основании Устава"
            />
          </div>
        </div>
      </div>

      {/* Исполнитель */}
      <div>
        <p className="text-xs font-bold text-ink uppercase tracking-wide mb-2">Исполнитель</p>
        <div className="flex flex-col gap-3">
          <div>
            <label htmlFor="executor-name" className={LABEL_CLASS}>
              ФИО / Наименование
            </label>
            <input
              id="executor-name"
              type="text"
              value={state.executorName}
              onChange={(e) =>
                dispatch({
                  type: 'SET_FIELD',
                  update: { path: 'executorName', value: e.target.value },
                })
              }
              aria-label="ФИО или наименование исполнителя"
              data-testid="executor-name-input"
              className={INPUT_CLASS}
              placeholder="ИП «Иван Иванов» / Иванов И.И."
            />
          </div>

          <div>
            <label htmlFor="executor-rep-name" className={LABEL_CLASS}>
              В лице
              <span className={HINT_CLASS}>(если через представителя)</span>
            </label>
            <input
              id="executor-rep-name"
              type="text"
              value={state.executorRepName}
              onChange={(e) =>
                dispatch({
                  type: 'SET_FIELD',
                  update: { path: 'executorRepName', value: e.target.value },
                })
              }
              aria-label="Представитель исполнителя"
              data-testid="executor-rep-name-input"
              className={INPUT_CLASS}
              placeholder="Иванова И.И."
            />
          </div>

          <div>
            <label htmlFor="executor-rep-basis" className={LABEL_CLASS}>
              На основании
              <span className={HINT_CLASS}>(Устава / Доверенности и т.п.)</span>
            </label>
            <input
              id="executor-rep-basis"
              type="text"
              value={state.executorRepBasis}
              onChange={(e) =>
                dispatch({
                  type: 'SET_FIELD',
                  update: { path: 'executorRepBasis', value: e.target.value },
                })
              }
              aria-label="Основание полномочий представителя"
              data-testid="executor-rep-basis-input"
              className={INPUT_CLASS}
              placeholder="действующего на основании Устава"
            />
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
